import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaPlay } from 'react-icons/fa';
import { authFetch } from '../../utils/auth';

/**
 * WelcomeVideoThumbnail - clickable intro-video thumbnail shown beside the
 * post-onboarding welcome message. Dispatches the same 'open-video-modal'
 * event as the /resources video card, so playback goes through the shared
 * VideoPlayerModal (which resolves the SAS URL via /api/resources/:id/download).
 */
const WelcomeVideoThumbnail = ({ className = '' }) => {
  const resourceIdRef = useRef(null);
  const [resolving, setResolving] = useState(false);

  // The intro video is the single video-type Resource ("Velkommen til Coord");
  // its _id is assigned at seed time, so it must be looked up at runtime.
  const resolveVideoResourceId = useCallback(async () => {
    if (resourceIdRef.current) return resourceIdRef.current;

    const response = await authFetch('/resources');
    if (!response.ok) {
      throw new Error('Failed to fetch resources');
    }

    const resources = await response.json();
    const video =
      resources.find((r) => r.type === 'video' && /velkommen/i.test(r.title || '')) ||
      resources.find((r) => r.type === 'video');

    if (!video) {
      throw new Error('No welcome video resource found');
    }

    resourceIdRef.current = video._id;
    return video._id;
  }, []);

  // Warm the lookup so a tap opens the modal without waiting on the list fetch
  useEffect(() => {
    resolveVideoResourceId().catch(() => {});
  }, [resolveVideoResourceId]);

  const handleClick = async () => {
    setResolving(true);
    try {
      const resourceId = await resolveVideoResourceId();
      window.dispatchEvent(new CustomEvent('open-video-modal', {
        detail: { resourceId }
      }));
    } catch (err) {
      console.error('[WelcomeVideoThumbnail] Error resolving welcome video:', err);
      alert('Kunne ikke laste videoen. Vennligst prøv igjen.');
    } finally {
      setResolving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={resolving}
      aria-label="Se introduksjonsvideoen til Coord"
      className={`group relative block w-full overflow-hidden rounded-xl shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary transition-shadow ${className}`}
    >
      <img
        src="/images/resources/Velkommen-Thumbnail.png"
        alt=""
        className="w-full h-full object-cover"
        style={{ aspectRatio: '16/9' }}
        loading="lazy"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
        <span className="w-14 h-14 rounded-full bg-primary flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
          {resolving ? (
            <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
          ) : (
            <FaPlay className="w-5 h-5 text-white ml-1" />
          )}
        </span>
      </span>
    </button>
  );
};

export default WelcomeVideoThumbnail;
